import { CanvasHtml } from "@dinahmoe/dmafjs-visual";
import GUI from "lil-gui";

class GradientWebGLEnvironment extends CanvasHtml {
  handlers = this.extendParentHandlers({
    scrollOffset: { handler: this.updateScrollOffset, type: "float", default: 0 },
    scrollSpeed: { handler: this.updateScrollSpeed, type: "float", default: 0.6666666667 },
    colorMap: { handler: this.updateColorMap, type: "object", default: [] },
    rootHeight: { handler: this.rebuildColorMap, type: "float", default: 0 },
    transition1: { handler: this.rebuildColorMap, type: "float", default: 0 },
    transition2: { handler: this.rebuildColorMap, type: "float", default: 0 },
    transition3: { handler: this.rebuildColorMap, type: "float", default: 0 },
    transition4: { handler: this.rebuildColorMap, type: "float", default: 0 },
    dotScrollSpeed: { handler: this.updateDotConfig, type: "float", default: 0.75 },
    dotSpacing: { handler: this.updateDotConfig, type: "float", default: 72 },
    dotSize: { handler: this.updateDotConfig, type: "float", default: 2 },
    dotOpacity: { handler: this.updateDotConfig, type: "float", default: 0.5 },
  });

  updateScrollOffset(eNode) {
    if (eNode._config) {
      const speed = eNode.props.scrollSpeed || 0.6666666667;
      eNode._config.scrollOffset = -eNode.props.scrollOffset * speed;
      eNode._config.dotScrollOffset = eNode.props.scrollOffset * (eNode.props.dotScrollSpeed || 0.75);
    }
  }

  updateDotConfig(eNode) {
    if (eNode._config) {
      const p = eNode.props;
      eNode._config.dotScrollSpeed = p.dotScrollSpeed;
      eNode._config.dotSpacing = p.dotSpacing;
      eNode._config.dotSize = p.dotSize;
      eNode._config.dotOpacity = p.dotOpacity;
      this.updateScrollOffset(eNode);
    }
  }

  updateScrollSpeed(eNode) {
    if (eNode._config) {
      eNode._config.scrollSpeed = eNode.props.scrollSpeed;
      this.updateScrollOffset(eNode);
    }
  }

  updateColorMap(eNode) {
    if (eNode._config && eNode.props.colorMap) {
      eNode._config.colorMap = eNode.props.colorMap;
    }
  }

  rebuildColorMap(eNode) {
    if (!eNode._config) return;
    const props = eNode.props;
    eNode._config.rootHeight = props.rootHeight;
    eNode._config.colorMap = this.buildColorMap(props);
  }

  buildColorMap(props) {
    const rootHeight = props.rootHeight || 1080;
    const t = rootHeight * 0.1;

    const blue = "7429CC";
    const pink = "E88EA6";
    const red = "CC2237";
    const magenta = "CB298D";

    const p1 = props.transition1;
    const p2 = props.transition2;
    const p3 = props.transition3;
    const p4 = props.transition4;

    return [
      // Section 1: blue — starts above viewport
      { color: blue, position: -rootHeight / 3 },
      // Transition 1: blue → magenta
      { color: blue, position: p1 - 1.5 * t },
      { color: pink, position: p1 - 0.5 * t },
      { color: red,  position: p1 + 0.5 * t },
      // Section 2: magenta
      { color: magenta, position: p1 + 1.5 * t },
      // Transition 2: magenta → blue
      { color: magenta, position: p2 - 1.5 * t },
      { color: red,     position: p2 - 0.5 * t },
      { color: pink,    position: p2 + 0.5 * t },
      // Section 3: blue
      { color: blue, position: p2 + 1.5 * t },
      // Transition 3: blue → magenta
      { color: blue, position: p3 - 1.5 * t },
      { color: pink, position: p3 - 0.5 * t },
      { color: red,  position: p3 + 0.5 * t },
      // Section 4: magenta
      { color: magenta, position: p3 + 1.5 * t },
      // Transition 4: magenta → blue
      { color: magenta, position: p4 - 1.5 * t },
      { color: red,     position: p4 - 0.5 * t },
      { color: pink,    position: p4 + 0.5 * t },
      // Section 5: blue (footer)
      { color: blue, position: p4 + 1.5 * t },
      { color: blue, position: p4 + 1.5 * t + rootHeight },
    ];
  }

  setProps(node) {
    super.setProps(node);

    const canvas = node.element;
    const canvasWidth = node.props.canvasWidth || 960;
    const aspectRatio = (canvas.clientHeight || 1080) / (canvas.clientWidth || 1920);
    canvas.width = canvasWidth;
    canvas.height = Math.round(canvasWidth * aspectRatio);

    const gl = canvas.getContext("webgl2", {
      antialias: false,
      preserveDrawingBuffer: true,
      alpha: false
    });
    if (!gl) {
      console.error("WebGL 2.0 not supported");
      return;
    }

    // Configuration
    const config = {
      waveStrength: 0.158,
      blurEffectStrength: 0.5,
      ditherStrength: 10.0,
      noiseOctaves: 4,
      noiseInfluence: 0.659,
      animationSpeed: 0.2,
      peakAmplitude: 0.692,
      peakSpeed: 0.1,
      grainEnabled: false,
      grainOpacity: 0.0686,
      grainPixelSize: 1,
      grainDensity: 0.3,
      scrollOffset: -node.props.scrollOffset * node.props.scrollSpeed,
      scrollSpeed: node.props.scrollSpeed,
      rootHeight: node.props.rootHeight,
      dotScrollOffset: node.props.scrollOffset * node.props.dotScrollSpeed,
      dotScrollSpeed: node.props.dotScrollSpeed,
      dotSpacing: node.props.dotSpacing,
      dotSize: node.props.dotSize,
      dotOpacity: node.props.dotOpacity,
    };

    // Static mode: disable all animation
    const reducedMotion = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (node.props.staticMode || reducedMotion) {
      config.animationSpeed = 0;
      config.peakAmplitude = 0;
      config.peakSpeed = 0;
      config.ditherStrength = 0;
      config.grainEnabled = false;
      config.dotOpacity = 0;
    }

    // Generate color map from transition positions or use provided colorMap
    if (node.props.colorMap && node.props.colorMap.length > 0) {
      config.colorMap = node.props.colorMap;
    } else {
      config.colorMap = this.buildColorMap(node.props);
    }

    // Store config on node so component can update it
    node._config = config;

    // Create GUI
    const gui = new GUI({ title: "Gradient Controls (WebGL)", width: 300 });
    gui.domElement.style.position = "fixed";
    gui.domElement.style.top = "10px";
    gui.domElement.style.left = "10px";
    gui.domElement.style.display = "none";

    const waveFolder = gui.addFolder("Wave Animation");
    waveFolder.add(config, "waveStrength", 0, 1).name("Wave Strength");
    waveFolder.add(config, "animationSpeed", 0, 1).name("Animation Speed (x1000)");
    waveFolder.add(config, "noiseInfluence", 0, 1).name("Noise Influence");
    waveFolder.add(config, "noiseOctaves", 1, 10, 1).name("Noise Octaves");

    const effectsFolder = gui.addFolder("Effects");
    effectsFolder.add(config, "peakAmplitude", 0, 2).name("Peak Amplitude");
    effectsFolder.add(config, "peakSpeed", 0, 1).name("Peak Speed (x1000)");
    effectsFolder.add(config, "blurEffectStrength", 0, 1).name("Blur Effect");
    effectsFolder.add(config, "ditherStrength", 0, 50).name("Dither Strength");

    const grainFolder = gui.addFolder("Grain Effect");
    grainFolder.add(config, "grainEnabled").name("Enabled");
    grainFolder.add(config, "grainOpacity", 0, 0.1).name("Opacity");
    grainFolder.add(config, "grainPixelSize", 1, 10, 1).name("Pixel Size");
    grainFolder.add(config, "grainDensity", 0, 0.3).name("Density");

    gui.add(config, "scrollOffset", -1000, 1000).name("Scroll Offset");

    node._gui = gui;

    // Vertex shader - simple passthrough
    const vertexShaderSource = `#version 300 es
      in vec2 a_position;
      void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
      }
    `;

    // Fragment shader - all gradient logic runs on GPU
    const fragmentShaderSource = `#version 300 es
      precision highp float;

      out vec4 fragColor;

      uniform vec2 u_resolution;
      uniform float u_time;
      uniform float u_frameCount;
      uniform vec3 u_colorStops[50];
      uniform float u_colorPositions[50];
      uniform float u_transitionCenters[50];
      uniform float u_zoneIndices[50];
      uniform int u_colorStopCount;
      uniform float u_waveStrength;
      uniform float u_blurEffect;
      uniform float u_ditherStrength;
      uniform int u_noiseOctaves;
      uniform float u_noiseInfluence;
      uniform float u_scrollOffset;
      uniform bool u_grainEnabled;
      uniform float u_grainOpacity;
      uniform float u_grainDensity;
      uniform float u_grainPixelSize;
      uniform float u_rawTime;
      uniform float u_rootHeight;
      uniform float u_peakAmplitude;
      uniform float u_peakSpeed;
      uniform float u_dotScrollOffset;
      uniform float u_dotSpacing;
      uniform float u_dotSize;
      uniform float u_dotOpacity;

      // Bob Jenkins' One-At-A-Time hashing algorithm
      uint hash(uint x) {
        x += (x << 10u);
        x ^= (x >> 6u);
        x += (x << 3u);
        x ^= (x >> 11u);
        x += (x << 15u);
        return x;
      }

      // Compound hash for vec2
      uint hash(uvec2 v) {
        return hash(v.x ^ hash(v.y));
      }

      // Construct float with half-open range [0:1] using low 23 bits
      float floatConstruct(uint m) {
        const uint ieeeMantissa = 0x007FFFFFu;
        const uint ieeeOne = 0x3F800000u;
        m &= ieeeMantissa;
        m |= ieeeOne;
        float f = uintBitsToFloat(m);
        return f - 1.0;
      }

      // Pseudo-random value in half-open range [0:1]
      float random(vec2 v) {
        return floatConstruct(hash(floatBitsToUint(v)));
      }

      float random3(vec3 v) {
        uvec3 uv = floatBitsToUint(v);
        return floatConstruct(hash(uv.x ^ hash(uv.y) ^ hash(uv.z)));
      }

      // Fractal noise (traveling wave)
      float fractalNoise(float x, float t, float baseFreq, float timeSpeed) {
        float val = 0.0;
        float frequency = baseFreq;
        float amplitude = 1.0;
        float persistence = 0.5;

        for (int i = 0; i < 10; i++) {
          if (i >= u_noiseOctaves) break;
          val += sin((x * frequency) + (t * timeSpeed)) * amplitude;
          frequency *= 2.0;
          amplitude *= persistence;
        }
        return val;
      }

      void main() {
        vec2 uv = gl_FragCoord.xy;
        float x = uv.x;
        float y = u_resolution.y - uv.y; // Flip Y coordinate

        // Calculate wave offset (traveling wave, moves left to right)
        float baseSineWave = sin((x * 0.002) - u_time);
        float detailedNoise = fractalNoise(x, u_time, 0.0025, -0.0008);
        float baseWave = (baseSineWave * (u_waveStrength * (1.0 - u_noiseInfluence))) +
                         (detailedNoise * (u_waveStrength * u_noiseInfluence));

        float effectiveY = y - baseWave;

        // Scale from canvas pixels to CSS pixels
        float cssY = effectiveY * (u_rootHeight / u_resolution.y);

        // Band width variation — each transition zone has its own peak wave
        // Zone index offsets the phase so zones expand/contract independently
        float basePeakPhase = (x / u_resolution.x) * 3.14159 + u_rawTime * u_peakSpeed;

        float adjustedPositions[50];
        for (int i = 0; i < 50; i++) {
          if (i >= u_colorStopCount) break;
          float zonePhase = basePeakPhase + u_zoneIndices[i] * 2.094; // 2π/3 offset per zone
          float peak = sin(zonePhase);
          float scale = 1.0 + peak * u_peakAmplitude;
          adjustedPositions[i] = u_transitionCenters[i] + (u_colorPositions[i] - u_transitionCenters[i]) * scale;
        }

        // Apply scroll offset last — purely a viewport shift
        // Crossfade between dither patterns
        float ditherPhase = u_rawTime / 100.0;
        float dither = mix(random3(vec3(uv, floor(ditherPhase))) - 0.5, random3(vec3(uv, floor(ditherPhase) + 1.0)) - 0.5, fract(ditherPhase));
        float gradientY = cssY - u_scrollOffset + (dither * u_ditherStrength);

        vec3 color;

        // Find which two adjusted stops we're between
        if (gradientY <= adjustedPositions[0]) {
          color = u_colorStops[0];
        }
        else if (gradientY >= adjustedPositions[u_colorStopCount - 1]) {
          color = u_colorStops[u_colorStopCount - 1];
        }
        else {
          for (int i = 0; i < 49; i++) {
            if (i >= u_colorStopCount - 1) break;

            if (gradientY >= adjustedPositions[i] && gradientY < adjustedPositions[i + 1]) {
              float segmentStart = adjustedPositions[i];
              float segmentEnd = adjustedPositions[i + 1];
              float segmentLength = segmentEnd - segmentStart;

              float progress = (gradientY - segmentStart) / max(segmentLength, 1.0);

              // Apply blur effect (S-curve)
              progress = progress - (u_blurEffect * sin(progress * 3.14159) * (1.0 - progress));
              progress = clamp(progress, 0.0, 1.0);

              color = mix(u_colorStops[i], u_colorStops[i + 1], progress);
              break;
            }
          }
        }

        // Apply color dither — adds variation in solid color areas too
        if (u_ditherStrength > 0.0) {
          color += vec3(dither * 0.05);
        }

        // Add grain
        if (u_grainEnabled) {
          // Snap to grain pixel grid
          vec2 grainUv = floor(uv / u_grainPixelSize) * u_grainPixelSize;
          // Use spatial + temporal hash for true randomness
          vec3 grainInput = vec3(grainUv, u_frameCount);
          float grain = random3(grainInput);
          if (grain < u_grainDensity) {
            color = mix(color, vec3(1.0), u_grainOpacity);
          }
        }

        // Dot matrix overlay — visible only in first magenta section
        if (u_dotOpacity > 0.0 && u_dotSpacing > 0.0) {
          float dotStart = adjustedPositions[4];
          float dotEnd = adjustedPositions[5];
          float dotFactor = smoothstep(dotStart - 20.0, dotStart + 20.0, gradientY)
                          * (1.0 - smoothstep(dotEnd - 20.0, dotEnd + 20.0, gradientY));

          if (dotFactor > 0.0) {
            float dotScale = u_resolution.y / u_rootHeight;
            float spacing = u_dotSpacing * dotScale;
            float radius = u_dotSize * dotScale;

            // Dot grid scrolls independently from gradient
            float dotY = y + u_dotScrollOffset * dotScale;
            float dotX = x;

            // Anchor grid to canvas center so dots stay fixed on resize
            float dotXFromCenter = dotX - u_resolution.x * 0.5;

            // Distance to nearest dot center
            float cellX = mod(dotXFromCenter, spacing) - spacing * 0.5;
            float cellY = mod(dotY, spacing) - spacing * 0.5;
            float dist = length(vec2(cellX, cellY));

            // Soft-edged dot
            float dotAlpha = 1.0 - smoothstep(radius - 0.5, radius + 0.5, dist);

            color = mix(color, vec3(1.0), dotFactor * dotAlpha * u_dotOpacity);
          }
        }

        fragColor = vec4(color, 1.0);
      }
    `;

    // Compile shader
    function compileShader(source, type) {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error("Shader compile error:", gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    }

    const vertexShader = compileShader(vertexShaderSource, gl.VERTEX_SHADER);
    const fragmentShader = compileShader(fragmentShaderSource, gl.FRAGMENT_SHADER);

    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error("Program link error:", gl.getProgramInfoLog(program));
      return;
    }

    gl.useProgram(program);

    // Set up full-screen quad with explicit VAO
    const vao = gl.createVertexArray();
    gl.bindVertexArray(vao);

    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);

    const positionLocation = gl.getAttribLocation(program, "a_position");
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    // Get uniform locations
    const uniforms = {
      resolution: gl.getUniformLocation(program, "u_resolution"),
      time: gl.getUniformLocation(program, "u_time"),
      frameCount: gl.getUniformLocation(program, "u_frameCount"),
      colorStops: gl.getUniformLocation(program, "u_colorStops"),
      colorPositions: gl.getUniformLocation(program, "u_colorPositions"),
      transitionCenters: gl.getUniformLocation(program, "u_transitionCenters"),
      zoneIndices: gl.getUniformLocation(program, "u_zoneIndices"),
      colorStopCount: gl.getUniformLocation(program, "u_colorStopCount"),
      waveStrength: gl.getUniformLocation(program, "u_waveStrength"),
      blurEffect: gl.getUniformLocation(program, "u_blurEffect"),
      ditherStrength: gl.getUniformLocation(program, "u_ditherStrength"),
      noiseOctaves: gl.getUniformLocation(program, "u_noiseOctaves"),
      noiseInfluence: gl.getUniformLocation(program, "u_noiseInfluence"),
      scrollOffset: gl.getUniformLocation(program, "u_scrollOffset"),
      grainEnabled: gl.getUniformLocation(program, "u_grainEnabled"),
      grainOpacity: gl.getUniformLocation(program, "u_grainOpacity"),
      grainDensity: gl.getUniformLocation(program, "u_grainDensity"),
      grainPixelSize: gl.getUniformLocation(program, "u_grainPixelSize"),
      rawTime: gl.getUniformLocation(program, "u_rawTime"),
      rootHeight: gl.getUniformLocation(program, "u_rootHeight"),
      peakAmplitude: gl.getUniformLocation(program, "u_peakAmplitude"),
      peakSpeed: gl.getUniformLocation(program, "u_peakSpeed"),
      dotScrollOffset: gl.getUniformLocation(program, "u_dotScrollOffset"),
      dotSpacing: gl.getUniformLocation(program, "u_dotSpacing"),
      dotSize: gl.getUniformLocation(program, "u_dotSize"),
      dotOpacity: gl.getUniformLocation(program, "u_dotOpacity"),
    };

    // Helper to convert hex to RGB
    function hexToRgb(hex) {
      const r = parseInt(hex.substring(0, 2), 16) / 255;
      const g = parseInt(hex.substring(2, 4), 16) / 255;
      const b = parseInt(hex.substring(4, 6), 16) / 255;
      return [r, g, b];
    }

    // Process colorMap into arrays for shader
    function processColorMap(colorMap) {
      const colors = [];
      const positions = [];

      for (const stop of colorMap) {
        colors.push(...hexToRgb(stop.color));
        positions.push(stop.position);
      }

      // Compute transition centers — all stops in a transition zone (including boundaries)
      // scale from the zone center so all segments expand equally
      const transitionCenters = new Array(colorMap.length);
      const isTransition = (idx) => {
        if (idx <= 0 || idx >= colorMap.length - 1) return false;
        return colorMap[idx].color !== colorMap[idx - 1].color &&
               colorMap[idx].color !== colorMap[idx + 1].color;
      };

      // Default: each stop uses its own position (no scaling), zone index 0
      const zoneIndices = new Array(colorMap.length);
      for (let i = 0; i < colorMap.length; i++) {
        transitionCenters[i] = positions[i];
        zoneIndices[i] = 0;
      }

      // Find transition zones and assign zone center + zone index to all stops in the zone
      let i = 0;
      let zoneCount = 0;
      while (i < colorMap.length) {
        if (isTransition(i)) {
          const start = Math.max(0, i - 1);
          let end = i;
          while (end < colorMap.length - 1 && isTransition(end + 1)) end++;
          end = Math.min(colorMap.length - 1, end + 1);

          const center = (positions[start] + positions[end]) / 2;
          for (let j = start; j <= end; j++) {
            transitionCenters[j] = center;
            zoneIndices[j] = zoneCount + 1; // 1-based so 0 means "no zone"
          }
          zoneCount++;
          i = end + 1;
        } else {
          i++;
        }
      }

      return { colors, positions, transitionCenters, zoneIndices, count: colorMap.length };
    }

    node._isPaused = false;

    // Detect native scroll lazily — element may not exist yet at init time
    let scrollContainer = null;
    config._usesNativeScroll = false;

    function detectNativeScroll() {
      if (scrollContainer) return;
      const el = document.getElementById("container_content");
      if (el) {
        scrollContainer = el;
        config._usesNativeScroll = getComputedStyle(el).overflowY === "scroll";
      }
    }

    // Render loop
    let frameCount = 0;
    function render(currentTime) {
      if (node._isPaused) {
        node._animationFrameId = requestAnimationFrame(render);
        return;
      }

      frameCount++;
      detectNativeScroll();

      // Keep canvas at configured width, match aspect ratio to element
      const targetWidth = node.props.canvasWidth || 960;
      const aspectRatio = (canvas.clientHeight || 1080) / (canvas.clientWidth || 1920);
      const targetHeight = Math.round(targetWidth * aspectRatio);
      if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
        canvas.width = targetWidth;
        canvas.height = targetHeight;
      }

      gl.viewport(0, 0, canvas.width, canvas.height);

      // Process colorMap
      const { colors, positions, transitionCenters, zoneIndices, count } = processColorMap(config.colorMap);

      // Set uniforms
      gl.uniform2f(uniforms.resolution, canvas.width, canvas.height);
      gl.uniform1f(uniforms.time, currentTime * config.animationSpeed / 1000);
      gl.uniform1f(uniforms.frameCount, frameCount);
      gl.uniform3fv(uniforms.colorStops, colors);
      gl.uniform1fv(uniforms.colorPositions, positions);
      gl.uniform1fv(uniforms.transitionCenters, transitionCenters);
      gl.uniform1fv(uniforms.zoneIndices, zoneIndices);
      gl.uniform1i(uniforms.colorStopCount, count);
      gl.uniform1f(uniforms.waveStrength, config.waveStrength * 400);
      gl.uniform1f(uniforms.blurEffect, config.blurEffectStrength);
      gl.uniform1f(uniforms.ditherStrength, config.ditherStrength);
      gl.uniform1i(uniforms.noiseOctaves, config.noiseOctaves);
      gl.uniform1f(uniforms.noiseInfluence, config.noiseInfluence);
      // Native scroll bypass disabled — using scroll proxy for both platforms
      // if (config._usesNativeScroll) {
      //   const scrollY = scrollContainer.scrollTop;
      //   config.scrollOffset = -scrollY * config.scrollSpeed;
      //   config.dotScrollOffset = scrollY * (config.dotScrollSpeed || 0.75);
      // }

      gl.uniform1f(uniforms.scrollOffset, config.scrollOffset);
      gl.uniform1i(uniforms.grainEnabled, config.grainEnabled ? 1 : 0);
      gl.uniform1f(uniforms.grainOpacity, config.grainOpacity);
      gl.uniform1f(uniforms.grainDensity, config.grainDensity);
      gl.uniform1f(uniforms.grainPixelSize, config.grainPixelSize);
      gl.uniform1f(uniforms.rawTime, currentTime);
      gl.uniform1f(uniforms.rootHeight, config.rootHeight);
      gl.uniform1f(uniforms.peakAmplitude, config.peakAmplitude);
      gl.uniform1f(uniforms.peakSpeed, config.peakSpeed / 1000);
      gl.uniform1f(uniforms.dotScrollOffset, config.dotScrollOffset);
      gl.uniform1f(uniforms.dotSpacing, config.dotSpacing);
      gl.uniform1f(uniforms.dotSize, config.dotSize);
      gl.uniform1f(uniforms.dotOpacity, config.dotOpacity);

      // Draw
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.bindVertexArray(vao);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      gl.flush();

      node._animationFrameId = requestAnimationFrame(render);
    }

    requestAnimationFrame(render);
  }

  destroy(node) {
    if (node._animationFrameId) {
      cancelAnimationFrame(node._animationFrameId);
      node._animationFrameId = null;
    }

    if (node._gui) {
      node._gui.destroy();
      node._gui = null;
    }

    super.destroy(node);
  }
}

const gradientWebGLEnvironment = new GradientWebGLEnvironment("visual:gradient-webgl");
export { GradientWebGLEnvironment, gradientWebGLEnvironment };
